import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bed, Bath, Maximize, ImageOff } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get localized "Luxury Seafront Villa" text
const getLuxuryVillaText = (lang) => {
  const texts = {
    en: 'Luxury Seafront Villa',
    de: 'Luxusvilla am Meer',
    pl: 'Luksusowa willa nad morzem',
    sv: 'Lyxvilla vid havet',
    cs: 'Luxusní vila u moře',
  };
  return texts[lang] || texts.en;
};

// Status badge configuration
const STATUS_CONFIG = {
  for_sale: {
    en: 'For Sale',
    de: 'Zu Verkaufen',
    pl: 'Na Sprzedaż',
    sv: 'Till Salu',
    cs: 'Na Prodej',
    bgColor: 'bg-emerald-500',
    textColor: 'text-white',
  },
  reserved: {
    en: 'Reserved',
    de: 'Reserviert',
    pl: 'Zarezerwowane',
    sv: 'Reserverad',
    cs: 'Rezervováno',
    bgColor: 'bg-amber-500',
    textColor: 'text-white',
  },
  sold: {
    en: 'Sold',
    de: 'Verkauft',
    pl: 'Sprzedane',
    sv: 'Såld',
    cs: 'Prodáno',
    bgColor: 'bg-red-500',
    textColor: 'text-white',
  },
  coming_soon: {
    en: 'Coming Soon',
    de: 'Demnächst',
    pl: 'Wkrótce',
    sv: 'Kommer Snart',
    cs: 'Již Brzy',
    bgColor: 'bg-blue-500',
    textColor: 'text-white',
  },
};

// Helper to get localized URL
const getLocalizedUrl = (path, lang) => {
  if (lang === 'en') return path;
  return `/${lang}${path}`;
};

export const StatusBadge = ({ status, size = 'normal' }) => {
  const { language } = useLanguage();
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.for_sale;
  
  const sizeClasses = size === 'small' 
    ? 'px-2 py-0.5 text-[10px]' 
    : 'px-3 py-1 text-xs';
  
  return (
    <span 
      className={`${config.bgColor} ${config.textColor} ${sizeClasses} font-medium uppercase tracking-wider inline-block`}
      data-testid={`status-badge-${status}`}
    >
      {config[language] || config.en}
    </span>
  );
};

const VillaCard = ({ villa, index = 0 }) => {
  const { language, t } = useLanguage();
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const title = villa[`title_${language}`] || villa.title_en;
  const location = villa[`location_${language}`] || villa.location_en;

  const formatPrice = (price, priceOnRequest) => {
    if (priceOnRequest) {
      return t('villa.priceOnRequest');
    }
    return new Intl.NumberFormat(language === 'de' ? 'de-DE' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Get main image URL - prioritize main_image, then first exterior image
  const getImageUrl = () => {
    // 1. Check for dedicated main_image
    if (villa.main_image && villa.main_image.url) {
      if (villa.main_image.url.startsWith('http')) {
        return villa.main_image.url;
      }
      return `${API_URL}${villa.main_image.url}`;
    }
    
    // 2. Get first exterior image
    if (villa.images && villa.images.length > 0) {
      const exteriorImages = villa.images
        .filter(img => (img.category || 'exterior') === 'exterior')
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      const mainImage = exteriorImages.length > 0 ? exteriorImages[0] : villa.images[0];
      
      if (mainImage.url && mainImage.url.startsWith('http')) {
        return mainImage.url;
      }
      if (mainImage.url) {
        return `${API_URL}${mainImage.url}`;
      }
      if (mainImage.storage_path) {
        return `${API_URL}${mainImage.storage_path}`;
      }
    }
    return null;
  };

  const imageUrl = getImageUrl();
  const showPlaceholder = !imageUrl || imageError;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`villa-card-${villa.id}`}
    >
      <Link to={getLocalizedUrl(`/villa/${villa.slug || villa.id}`, language)} className="group block">
        {/* Image with Status Badge */}
        <div className="image-hover aspect-[4/3] bg-gray-100 mb-6 relative overflow-hidden">
          {showPlaceholder ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
              <ImageOff className="w-12 h-12 text-gray-400 mb-2" />
              <span className="text-gray-400 text-sm">No Image</span>
            </div>
          ) : (
            <>
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="animate-pulse w-8 h-8 rounded-full bg-gray-300"></div>
                </div>
              )}
              <img
                src={imageUrl}
                alt={`${title} - ${location} - ${getLuxuryVillaText(language)}`}
                title={title}
                className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                referrerPolicy="no-referrer"
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            </>
          )}
          {/* Status Badge - positioned top-left */}
          {villa.status && (
            <div className="absolute top-3 left-3 z-10">
              <StatusBadge status={villa.status} size="normal" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="space-y-3">
          {/* Location */}
          <p className="label-style">{location}</p>

          {/* Title */}
          <h3 className="font-heading text-2xl md:text-3xl text-night group-hover:text-gold transition-colors duration-300">
            {title}
          </h3>

          {/* Features */}
          <div className="flex items-center gap-6 text-text-secondary text-sm">
            <span className="flex items-center gap-2">
              <Bed className="w-4 h-4" />
              {villa.bedrooms} {t('villas.bedrooms')}
            </span>
            <span className="flex items-center gap-2">
              <Bath className="w-4 h-4" />
              {villa.bathrooms} {t('villas.bathrooms')}
            </span>
            <span className="flex items-center gap-2">
              <Maximize className="w-4 h-4" />
              {villa.area} {t('villas.area')}
            </span>
          </div>

          {/* Price */}
          <p className="price-tag text-xl text-night">
            {formatPrice(villa.price, villa.price_on_request)}
          </p>
        </div>
      </Link>
    </motion.div>
  );
};

export default VillaCard;
